import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Stack,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from "@mui/material";

import { PersonOutlined, AccountBalance, ChangeCircle, CalendarMonth, ChangeHistory } from "@mui/icons-material";
import SettingsIcon from "@mui/icons-material/Settings";
import LockIcon from "@mui/icons-material/Lock";
import LogoutIcon from "@mui/icons-material/Logout";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import LogoSection from "../LogoSection";
import { logout, login } from "store/authReducer";
import { storeMenu } from "store/menuReducer";
import { exitImpersonation } from "apis/controllers/admin";
import decodeToken from "util/decode-token";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import SeasonDialog from "pages/govnr/Dashboard/SeasonDialog";
import { handleRoleSelection } from "store/roleReducer";
import { persistor } from "store/store";

const Header = ({ handleLeftDrawerToggle }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { seasonId } = useSelector(state => state.auth)
  const { roles } = useSelector(state => state.role)

  const { user, isLoggedIn, token } = useSelector((state) => state.auth);
  const data = decodeToken(token);

  // ---------- USER MENU STATE ----------
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [openSeasonDialog, setOpenSeasonDialog] = useState(false);
  const userMenuOpen = Boolean(userMenuAnchorEl);


  const handleUserMenuToggle = (event) => {
    if (user?.isCitizen) return;
    setUserMenuAnchorEl(userMenuAnchorEl ? null : event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };


  // ---------- MENU ACTIONS ----------
  const handleLogout = () => {
    dispatch(logout());
    persistor.purge();
    navigate("/login");
  };

  const goToSettings = () => {
    handleUserMenuClose();
    navigate("/govnr/user-settings");
  };

  const goToChangePassword = () => {
    handleUserMenuClose();
    navigate("/change-password");
  };

  const onSubmit = async () => {
    await exitImpersonation()
      .then((res) => {
        dispatch(storeMenu(res?.menu));
        dispatch(
          login({
            token: res?.token,
            refreshToken: res?.refreshToken,
            user: res?.user,
            seasonId: res?.season,
          })
        );
        navigate("/home");
      })
      .catch((error) => {
        console.log(error);
        enqueueSnackbar(error || "Παρουσιάστηκε Σφάλμα", {
          variant: "error",
        });
      });
  };

  const handleSeasonChange = (newSeason) => {
    // dispatch(setSeasonId(newSeason));
    globalThis.location.reload(); // or refetch dashboard data
  };

  return (
    <Box
      sx={{
        height: "31px",
        display: "flex",
        flexDirection: "row",
        width: "100%",
        alignItems: "center",
      }}
    >
      {/* LOGO */}
      <Box
        sx={{
          width: 228,
          display: "flex",
          [theme.breakpoints.down("md")]: {
            width: "auto",
          },
        }}
      >
        <Box component="span" sx={{ display: { xs: "none", md: "block" }, flexGrow: 1 }}>
          <Stack direction="row" spacing={2}>
            <LogoSection />
          </Stack>
        </Box>
      </Box>

      <Box sx={{ flex: 0.3 }} />

      {/* TITLE */}
      <Typography
        sx={{
          typography: { xs: "h6", sm: "h5", lg: "h4" },
          color: "#fefefe !important",
        }}
      >
        Λογαριασμός Αγροτικής Εστίας (ΛΑΕ)
      </Typography>

      <Box sx={{ flex: 1 }} />

      {/* RIGHT SIDE */}
      {isLoggedIn && (
        <Stack direction="row" gap={2} alignItems="center">
          {/* EXIT IMPERSONATION */}
          {data?.original_name && (
            <Button
              onClick={onSubmit}
              variant="contained"
              disableElevation
              color="error"
            >
              Exit Impersonation
            </Button>
          )}

          {/* USER MENU */}
          <Box
            onClick={(user?.canChangePassword || user?.canChangeUsername) ? (e) => handleUserMenuToggle(e) : () => { }}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: (user?.canChangePassword || user?.canChangeUsername) ? "pointer" : "default",
              "& *": { cursor: "inherit" },
            }}
          >
            <PersonOutlined sx={{ color: "#fefefe", mr: 0.5 }} />

            <Typography
              variant="h5"
              sx={{
                mt: 0.5,
                typography: { xs: "h6", sm: "h5", lg: "h5" },
                color: "#fefefe !important",
                userSelect: "none",
              }}
            >
              {user?.lastName} {user?.firstName}
            </Typography>

            <Menu
              anchorEl={userMenuAnchorEl}
              open={userMenuOpen}
              onClose={handleUserMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {roles?.length > 0 && <MenuItem onClick={() => {
                dispatch(handleRoleSelection(true))
              }}>
                <ListItemIcon>
                  <ChangeCircle fontSize="small" />
                </ListItemIcon>
                Αλλαγή Ρόλου
              </MenuItem>}

              <MenuItem onClick={() => setOpenSeasonDialog(true)}>
                <ListItemIcon>
                  <CalendarMonth fontSize="small" />
                </ListItemIcon>
                Αλλαγή Έτους
              </MenuItem>
              <Divider />
              <MenuItem onClick={goToSettings}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Ρυθμίσεις Λογαριασμού
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Έξοδος
              </MenuItem>
            </Menu>
          </Box>




          {/* INSTITUTE */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AccountBalance sx={{ color: "#fefefe" }} />
            <Typography variant="h5" sx={{ color: "#fefefe", ml: 0.5 }}>
              {user?.institute}
            </Typography>
          </Box>
        </Stack>
      )
      }
      <SeasonDialog
        open={openSeasonDialog}
        onClose={() => setOpenSeasonDialog(false)}
        currentSeason={seasonId}
        onChange={handleSeasonChange}
      />
    </Box >
  );
};

export default Header;
